import React, { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom'
import Dashboard  from './pages/Dashboard'
import TrackerList from './pages/TrackerList'
import LineSearch  from './pages/LineSearch'
import LineList    from './pages/LineList'
import UploadCSV   from './pages/UploadCSV'
import Settings    from './pages/Settings'
import Login       from './pages/Login'
import { onAuth, logout } from './utils/firebase'
import {
  Menu, X, LayoutDashboard, MapPin, Search,
  Smartphone, Upload, Settings as SettingsIcon, Radio, LogOut, Sparkles
} from 'lucide-react'
import Icon3D from './components/Icon3D'
import { APP_TAG } from './version'

const NAV = [
  { name: 'Dashboard',    href: '/',             icon: LayoutDashboard, color: 'amber'   },
  { name: 'Trackers',     href: '/trackers',     icon: MapPin,          color: 'blue'    },
  { name: 'Buscador',     href: '/search',       icon: Search,          color: 'violet'  },
  { name: 'TOKA SIM',     href: '/lines/toka',   icon: Radio,           color: 'red'     },
  { name: 'OUI',          href: '/lines/oui',    icon: Radio,           color: 'yellow'  },
  { name: 'Telcel B2B',   href: '/lines/telcel', icon: Smartphone,      color: 'sky'     },
  { name: 'ALLCE',        href: '/lines/allce',  icon: Smartphone,      color: 'purple'  },
  { name: 'AT&T',         href: '/lines/att',    icon: Smartphone,      color: 'cyan'    },
  { name: 'Otras',        href: '/lines/otras',  icon: Smartphone,      color: 'slate'   },
  { name: 'Importar CSV', href: '/upload',       icon: Upload,          color: 'emerald' },
  { name: 'Config',       href: '/settings',     icon: SettingsIcon,    color: 'gray'    },
]

const SECTION_TITLES = {
  '/':             'Dashboard',
  '/trackers':     'Trackers GPS',
  '/search':       'Buscador',
  '/lines/toka':   'TOKA SIM',
  '/lines/oui':    'OUI',
  '/lines/telcel': 'Telcel B2B',
  '/lines/allce':  'ALLCE',
  '/lines/att':    'AT&T',
  '/lines/otras':  'Otras líneas',
  '/upload':       'Importar CSV',
  '/settings':     'Configuración',
}

function pageTitle(pathname) {
  if (pathname === '/') return 'Dashboard'
  const match = Object.keys(SECTION_TITLES)
    .filter(k => k !== '/')
    .sort((a, b) => b.length - a.length)
    .find(k => pathname.startsWith(k))
  return match ? SECTION_TITLES[match] : 'Dashboard'
}

function Clock() {
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])
  return (
    <span className="hidden md:inline-flex items-center gap-2 text-xs text-gray-400 font-mono">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      {now.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
    </span>
  )
}

function Sidebar({ onClose, user }) {
  const location = useLocation()
  return (
    <div className="flex flex-col h-full w-64 glass border-r border-white/10">
      {/* Logo */}
      <div className="flex items-center justify-between px-5 h-16 border-b border-white/10 shrink-0">
        <Link to="/" onClick={onClose} className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-brand-gradient flex items-center justify-center shadow-glow-amber">
            <Sparkles className="w-5 h-5 text-gray-950" />
          </div>
          <div className="leading-none">
            <span className="block text-lg font-extrabold brand-mark tracking-tight">YANGO</span>
            <span className="block text-[10px] text-gray-500 uppercase tracking-[0.2em]">Buscador</span>
          </div>
        </Link>
        {onClose && (
          <button onClick={onClose} className="lg:hidden p-1.5 rounded-lg hover:bg-white/10 text-gray-400">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
        {NAV.map(item => {
          const active = location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href))
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onClose}
              className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                active
                  ? 'bg-amber-400/15 text-amber-300 shadow-[inset_0_0_0_1px_rgba(255,184,0,0.25)]'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-brand-gradient" />}
              <Icon3D icon={item.icon} color={item.color} size="sm" />
              {item.name}
            </Link>
          )
        })}
      </nav>

      {/* User + footer */}
      <div className="px-3 py-4 border-t border-white/10 shrink-0 space-y-3">
        <div className="flex items-center gap-3 px-2">
          <div className="w-9 h-9 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-sm font-bold text-amber-300 shrink-0">
            {(user?.email || 'Y').charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-gray-200 truncate">{user?.email || 'Usuario'}</p>
            <p className="text-[10px] text-gray-500">{APP_TAG}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs text-gray-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
        >
          <Icon3D icon={LogOut} color="rose" size="sm" /> Cerrar sesión
        </button>
      </div>
    </div>
  )
}

export default function App() {
  const [user, setUser] = useState(undefined)

  useEffect(() => {
    return onAuth(u => setUser(u))
  }, [])

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#0a0b10] flex items-center justify-center">
        <div className="w-9 h-9 border-[3px] border-amber-400 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      {user ? <Layout user={user} /> : <Login />}
    </Router>
  )
}

function Layout({ user }) {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  return (
    <div className="flex h-screen overflow-hidden">

      {/* Desktop sidebar */}
      <div className="hidden lg:flex shrink-0">
        <Sidebar onClose={null} user={user} />
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setOpen(false)} />
      )}
      <div className={`
        fixed inset-y-0 left-0 z-50 lg:hidden transform transition-transform duration-300
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar onClose={() => setOpen(false)} user={user} />
      </div>

      {/* Main */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">

        {/* Topbar */}
        <header className="h-16 shrink-0 glass border-b border-white/10 flex items-center justify-between px-4 lg:px-7">
          <div className="flex items-center gap-3">
            <button onClick={() => setOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-white/10 text-gray-300">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white leading-none">{pageTitle(location.pathname)}</h1>
              <p className="text-[11px] text-gray-500 mt-0.5">YANGO · Buscador de líneas GPS</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-gray-500">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> En línea
            </span>
            <Clock />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          <div key={location.pathname} className="page-fade p-5 lg:p-7 max-w-screen-2xl mx-auto">
            <Routes location={location}>
              <Route path="/"            element={<Dashboard  />} />
              <Route path="/trackers"    element={<TrackerList />} />
              <Route path="/search"      element={<LineSearch  />} />
              <Route path="/lines/:type" element={<LineList    />} />
              <Route path="/upload"      element={<UploadCSV   />} />
              <Route path="/settings"    element={<Settings    />} />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  )
}
